import { useCustomAudiencesApi, useShowSpinner } from 'hooks';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useShallow } from 'zustand/react/shallow';
import useCustomAudiencesRequest from '../../hooks/useCustomAudiencesRequest';
import useVisiblePolling, { useBoundedPollInterval } from '../../hooks/useVisiblePolling';
import { buildAudienceRows, statusKeepsPolling } from '../../lib/buildAudienceRows';
import { isTransientStatus } from '../../lib/loadErrorMessage';
import { logError } from '../../lib/logError';
import { DEFINITIONS_PAGE_SIZE } from '../../store/customAudiencesSlice';
import useCustomAudiencesStore from '../../store/useCustomAudiencesStore';
import AudienceListView from './AudienceList.View';

const AudienceList = () => {
  const ctx = useCustomAudiencesApi();
  const navigate = useNavigate();
  const request = useCustomAudiencesRequest();
  const state = useCustomAudiencesStore(
    useShallow(store => ({
      definitions: store.definitions,
      definitionsTotal: store.definitionsTotal,
      definitionsPageNumber: store.definitionsPageNumber,
      definitionsStatus: store.definitionsStatus,
      definitionsError: store.definitionsError,
      definitionsErrorStatus: store.definitionsErrorStatus,
      latestSnapshotByDefinitionId: store.latestSnapshotByDefinitionId,
      latestSnapshotErrorByDefinitionId: store.latestSnapshotErrorByDefinitionId,
      destinationsByDefinitionId: store.destinationsByDefinitionId,
      destinationsErrorByDefinitionId: store.destinationsErrorByDefinitionId,
      deliveriesBySnapshotId: store.deliveriesBySnapshotId,
      deliveriesErrorBySnapshotId: store.deliveriesErrorBySnapshotId,
      updatingDefinitionId: store.updatingDefinitionId,
      loadDefinitions: store.loadDefinitions,
      setDefinitionStatus: store.setDefinitionStatus
    }))
  );
  const { loadDefinitions } = state;

  /* Spinner on first load only; refreshes and polls update in place */
  useShowSpinner({ show: state.definitionsStatus === 'loading' });

  useEffect(() => {
    if (!ctx.brandId) return;
    loadDefinitions(ctx, { pageNumber: 1 }).catch(error => logError('list.load', error));
  }, [ctx, loadDefinitions]);

  const rows = useMemo(
    () =>
      buildAudienceRows({
        definitions: state.definitions,
        latestSnapshotByDefinitionId: state.latestSnapshotByDefinitionId,
        latestSnapshotErrorByDefinitionId: state.latestSnapshotErrorByDefinitionId,
        destinationsByDefinitionId: state.destinationsByDefinitionId,
        destinationsErrorByDefinitionId: state.destinationsErrorByDefinitionId,
        deliveriesBySnapshotId: state.deliveriesBySnapshotId,
        deliveriesErrorBySnapshotId: state.deliveriesErrorBySnapshotId
      }),
    [
      state.definitions,
      state.latestSnapshotByDefinitionId,
      state.latestSnapshotErrorByDefinitionId,
      state.destinationsByDefinitionId,
      state.destinationsErrorByDefinitionId,
      state.deliveriesBySnapshotId,
      state.deliveriesErrorBySnapshotId
    ]
  );

  /*
   * A stale row keeps its last known status, so it polls while that status is in progress.
   * An Unknown row, or a failed list load, polls only while its failure is transient.
   * A permanent failure such as a 403 waits for Retry or a new visit.
   */
  const polling =
    (state.definitionsStatus === 'error' && isTransientStatus(state.definitionsErrorStatus)) ||
    rows.some(statusKeepsPolling);
  const { intervalMs } = useBoundedPollInterval(polling, ctx.brandId);
  useVisiblePolling(
    signal => loadDefinitions({ ...ctx, signal }).catch(error => logError('list.poll', error)),
    intervalMs,
    polling,
    ctx.brandId
  );

  const [retrying, setRetrying] = useState(false);
  const onRetry = () => {
    setRetrying(true);
    loadDefinitions(ctx)
      .catch(error => logError('list.retry', error))
      .finally(() => setRetrying(false));
  };

  const onSetPaused = async (definitionId: number, paused: boolean) => {
    /* setDefinitionStatus patches the row in place; polling picks up any later status change */
    await request.run(
      'status',
      () => state.setDefinitionStatus(ctx, definitionId, paused ? 'paused' : 'active'),
      {
        success: paused ? 'Audience paused' : 'Audience resumed',
        error: "Couldn't update the audience"
      }
    );
  };

  return (
    <AudienceListView
      rows={rows}
      loading={state.definitionsStatus === 'loading' || state.definitionsStatus === 'idle'}
      error={state.definitionsError}
      retrying={retrying}
      onRetry={onRetry}
      updatingDefinitionId={state.updatingDefinitionId}
      pageNumber={state.definitionsPageNumber}
      pageCount={Math.max(1, Math.ceil(state.definitionsTotal / DEFINITIONS_PAGE_SIZE))}
      onPageChange={pageNumber =>
        loadDefinitions(ctx, { pageNumber }).catch(error => logError('list.page', error))
      }
      onNewAudience={() => navigate('new')}
      onOpenDefinition={definitionId => navigate(String(definitionId))}
      onSetPaused={onSetPaused}
    />
  );
};

export default AudienceList;
